import {
  OpenFeature,
  ProviderEvents,
  ProviderStatus,
  useBooleanFlagValue,
  useOpenFeatureClientStatus,
  useStringFlagValue,
} from '@openfeature/react-sdk';
import { useCallback, useSyncExternalStore } from 'react';

import { getFeatureFlagsConfig } from './config';
import { FEATURE_FLAG_DOMAIN } from './read';

/**
 * Boolean flag hook — a WRAPPER over `useBooleanFlagValue`, not a re-export
 * (design D8): the injected `forceEnableAll` override is honored at this one
 * choke point instead of being re-read from env across declarations.
 *
 * Takes NO domain parameter — the domain is INHERITED from the React tree
 * (design D7/D8), so a subtree wrapped in `<OpenFeatureProvider domain="…">`
 * resolves through that domain's provider using these same standard hooks.
 * Pinning the default domain here would break those subtrees — none exist
 * today, but the inheritance is the door's contract, not an accident.
 */
export const useBooleanFlag = (key: string, defaultValue: boolean): boolean => {
  const value = useBooleanFlagValue(key, defaultValue);
  return getFeatureFlagsConfig().forceEnableAll || value;
};

/**
 * One shared subscription for every `useBooleanFlagLite` reader, notified on
 * the provider events the SDK hook re-evaluates on. Each reader's store
 * snapshot is its flag's VALUE, so a notification re-renders only the readers
 * whose value actually changed (`useSyncExternalStore` compares snapshots with
 * `Object.is`) — the SDK hook's `isEqual` bail-out, without its per-instance
 * handlers.
 *
 * Every event the SDK hook reacts to is subscribed: Ready, ContextChanged and
 * ConfigurationChanged for value changes, and Error / Stale / Reconciling for
 * re-render parity with `useOpenFeatureClientStatus`. Note what the status
 * events do NOT do: web-sdk 1.10 short-circuits to the default only while
 * NOT_READY or FATAL (`shortCircuitIfNotReady`); in ERROR / STALE / RECONCILING
 * the client still asks the provider, which answers from its in-memory flags.
 * So neither an SDK reader nor a lite reader falls back to the default on a
 * provider error — a kill gate read through here keeps the provider's value.
 */
// Resolved on first subscribe, not at module load: suites that mock the SDK
// without `ProviderEvents` import this module too, and must not throw for it.
const flagEpochEvents = () => [
  ProviderEvents.Ready,
  ProviderEvents.ContextChanged,
  ProviderEvents.ConfigurationChanged,
  ProviderEvents.Error,
  ProviderEvents.Stale,
  ProviderEvents.Reconciling,
];
const flagEpochListeners = new Set<() => void>();
let detachFlagEpochHandlers: (() => void) | null = null;
const notifyFlagReaders = () => {
  flagEpochListeners.forEach((notify) => notify());
};
const subscribeToFlagEpoch = (listener: () => void): (() => void) => {
  flagEpochListeners.add(listener);
  if (!detachFlagEpochHandlers) {
    const events = flagEpochEvents();
    for (const event of events) OpenFeature.addHandler(event, notifyFlagReaders);
    detachFlagEpochHandlers = () => {
      for (const event of events) OpenFeature.removeHandler(event, notifyFlagReaders);
    };
    // The first reader rendered before these handlers existed (subscribe runs
    // in its passive effect). An event in that window — Ready, typically, at
    // boot — would be lost. Notify once so that reader re-reads its value now
    // (and re-renders only if it changed).
    notifyFlagReaders();
  }
  return () => {
    flagEpochListeners.delete(listener);
  };
};

// The same read `readFlag` performs, spelled out here: the ruling-36 guard
// treats a `readFlag(…)` CALL anywhere but a getter module as a raw read.
const readBooleanFlagNow = (key: string, defaultValue: boolean): boolean => {
  if (getFeatureFlagsConfig().forceEnableAll) return true;
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(key, defaultValue);
};

/** Test-only: how many lite readers are currently subscribed. */
export function __flagEpochListenerCountForTests(): number {
  return flagEpochListeners.size;
}

/** Test-only: drop every lite subscriber and detach the shared provider handlers. */
export function __resetFlagEpochForTests(): void {
  flagEpochListeners.clear();
  detachFlagEpochHandlers?.();
  detachFlagEpochHandlers = null;
}

/**
 * `useBooleanFlag` for a flag read from a hook that a large share of the tree
 * calls. The SDK hook does its work per INSTANCE — nine event handlers
 * registered on mount plus an evaluation — so one value read by hundreds of
 * components costs hundreds of registrations per render pass. This reads the
 * value synchronously (the same read as `readFlag`, honouring `forceEnableAll`)
 * as the store snapshot, over a single subscription shared by all readers.
 *
 * Same keys, same defaults, re-evaluated on the same provider events, and like
 * the SDK hook a reader re-renders only when its value changed. One deliberate
 * difference: the read is against the primary domain (`FEATURE_FLAG_DOMAIN`),
 * not the domain inherited from the React tree. No subtree uses another domain
 * today; a getter that must honour one keeps `useBooleanFlag`.
 */
export const useBooleanFlagLite = (key: string, defaultValue: boolean): boolean => {
  const getSnapshot = useCallback(
    () => readBooleanFlagNow(key, defaultValue),
    [key, defaultValue]
  );
  return useSyncExternalStore(subscribeToFlagEpoch, getSnapshot, getSnapshot);
};

/**
 * String flag hook — thin wrapper over `useStringFlagValue`. Unaffected by
 * `forceEnableAll` (boolean-only override). Domain is tree-inherited, as
 * above.
 */
export const useStringFlag = (key: string, defaultValue: string): string =>
  useStringFlagValue(key, defaultValue);

/**
 * Whether the OpenFeature provider has settled (READY or ERROR — anything
 * other than NOT_READY / RECONCILING). While `false`, flag hooks still return
 * their defaults, so callers that make irreversible decisions on a flag value
 * (e.g. the Healthdirect launch flow) should wait for `true` before acting
 * rather than treating an unresolved default as a settled read. ERROR counts
 * as resolved so such callers fall back to the flag default instead of
 * waiting forever. (Moved verbatim from `@/features/flags` at the F-series;
 * reads the tree-inherited domain's client status.)
 */
export const useAreFeatureFlagsResolved = () => {
  const flagStatus = useOpenFeatureClientStatus();
  return (
    flagStatus !== ProviderStatus.NOT_READY && flagStatus !== ProviderStatus.RECONCILING
  );
};
